"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/reusable_sections/Header";
const translations = {
  en: {
    sectionLabel: "§ FAQ / LEGAL BRIEFING",
    heading: "Expert legal insights.",
    categoryIndex: "※ CATEGORY INDEX",
    categories: [{
      id: "all",
      label: "ALL"
    }, {
      id: "compliance",
      label: "COMPLIANCE"
    }, {
      id: "immigration",
      label: "IMMIGRATION"
    }, {
      id: "haitian-law",
      label: "HAITIAN LAW"
    }, {
      id: "consultation",
      label: "CONSULTATION"
    }],
    entries: "ENTRIES",
    status: "STATUS",
    verified: "VERIFIED",
    directChannel: "※ DIRECT CHANNEL",
    directChannelHeading: "Need professional guidance?",
    bookConsultation: "BOOK CONSULTATION",
    faqs: [{
      id: "01",
      category: "compliance",
      question: "What does the FCFLC certification signify?",
      answer: "As a Foreign Legal Consultant certified by the Florida Bar, I am authorized to provide expert guidance on the laws of foreign jurisdictions, specifically focusing on Haitian legal frameworks and international compliance standards.",
      spec: "SN/01 · FLORIDA BAR · CERTIFIED"
    }, {
      id: "02",
      category: "immigration",
      question: "How can you assist with immigration documentation?",
      answer: "I provide comprehensive support in navigating complex immigration pathways, ensuring that all documentation is meticulously prepared, compliant with international standards, and aligned with your specific legal objectives.",
      spec: "SN/02 · DOCUMENTATION · GUIDANCE"
    }, {
      id: "03",
      category: "haitian-law",
      question: "Do you specialize in Haitian legal matters?",
      answer: "Yes, my practice is deeply rooted in Haitian law. I assist clients with property disputes, corporate compliance, and civil matters, bridging the gap between Haitian legal requirements and international expectations.",
      spec: "SN/03 · HAITIAN LAW · SPECIALIST"
    }, {
      id: "04",
      category: "consultation",
      question: "What is the value of your academic background?",
      answer: "My Master's degree in Criminal Justice and Immigration Studies from the University of North Florida provides a rigorous academic foundation that informs my practical approach to complex international legal challenges.",
      spec: "SN/04 · UNF · MASTER'S DEGREE"
    }, {
      id: "05",
      category: "compliance",
      question: "Are your consultations confidential?",
      answer: "Professional privilege and strict confidentiality are the cornerstones of my practice. All consultations are conducted with the highest level of discretion to protect your legal interests.",
      spec: "SN/05 · CONFIDENTIAL · SECURE"
    }, {
      id: "06",
      category: "consultation",
      question: "How do I schedule a paid consultation?",
      answer: "You can secure a dedicated consulting session through our integrated portal. Once payment is confirmed, you will receive a link to select a time that aligns with your schedule.",
      spec: "SN/06 · SECURE · BOOKING"
    }, {
      id: "07",
      category: "immigration",
      question: "Can you help with urgent immigration filings?",
      answer: "For time-sensitive immigration matters, I offer priority review services to ensure your filings are accurate and submitted within required windows, minimizing the risk of procedural delays.",
      spec: "SN/07 · PRIORITY · REVIEW"
    }, {
      id: "08",
      category: "haitian-law",
      question: "Do you provide services for corporate entities?",
      answer: "I support corporate clients in navigating foreign law compliance, ensuring that international business operations in Haiti remain within the bounds of local legal requirements and international treaties.",
      spec: "SN/08 · CORPORATE · COMPLIANCE"
    }]
  },
  fr: {
    sectionLabel: "§ FAQ / DOSSIER JURIDIQUE",
    heading: "Éclairages juridiques d'experts.",
    categoryIndex: "※ INDEX DES CATÉGORIES",
    categories: [{
      id: "all",
      label: "TOUS"
    }, {
      id: "compliance",
      label: "CONFORMITÉ"
    }, {
      id: "immigration",
      label: "IMMIGRATION"
    }, {
      id: "haitian-law",
      label: "DROIT HAÏTIEN"
    }, {
      id: "consultation",
      label: "CONSULTATION"
    }],
    entries: "ENTRÉES",
    status: "STATUT",
    verified: "VÉRIFIÉ",
    directChannel: "※ CANAL DIRECT",
    directChannelHeading: "Besoin de conseils professionnels ?",
    bookConsultation: "RÉSERVER UNE CONSULTATION",
    faqs: [{
      id: "01",
      category: "compliance",
      question: "Que signifie la certification FCFLC ?",
      answer: "En tant que consultant juridique étranger certifié par le Barreau de Floride, je suis autorisé à fournir des conseils d'experts sur les lois des juridictions étrangères, en particulier sur le cadre juridique haïtien et les normes internationales de conformité.",
      spec: "SN/01 · BARREAU DE FLORIDE · CERTIFIÉ"
    }, {
      id: "02",
      category: "immigration",
      question: "Comment pouvez-vous aider pour la documentation d'immigration ?",
      answer: "J'offre une assistance complète pour naviguer dans les parcours d'immigration complexes, en veillant à ce que tous les dossiers soient rigoureusement préparés, conformes aux normes internationales et adaptés à vos objectifs juridiques.",
      spec: "SN/02 · DOCUMENTATION · ORIENTATION"
    }, {
      id: "03",
      category: "haitian-law",
      question: "Êtes-vous spécialisé dans les affaires juridiques haïtiennes ?",
      answer: "Oui, mon expertise est profondément ancrée dans le droit haïtien. J'assiste les clients pour les litiges fonciers, la conformité d'entreprise et les affaires civiles, faisant le pont entre les exigences légales haïtiennes et les normes internationales.",
      spec: "SN/03 · DROIT HAÏTIEN · SPÉCIALISTE"
    }, {
      id: "04",
      category: "consultation",
      question: "Quelle est la valeur de votre formation académique ?",
      answer: "Mon Master en justice pénale et études sur l'immigration de l'University of North Florida constitue un socle académique solide qui guide mon approche pratique face aux enjeux juridiques internationaux.",
      spec: "SN/04 · UNF · MASTER"
    }, {
      id: "05",
      category: "compliance",
      question: "Vos consultations sont-elles confidentielles ?",
      answer: "Le secret professionnel et la confidentialité stricte sont les fondements de ma pratique. Toutes les consultations sont menées avec le plus haut niveau de discrétion pour protéger vos intérêts légaux.",
      spec: "SN/05 · CONFIDENTIEL · SÉCURISÉ"
    }, {
      id: "06",
      category: "consultation",
      question: "Comment planifier une consultation payante ?",
      answer: "Vous pouvez réserver une session de consultation dédiée via notre portail sécurisé. Dès la validation du paiement, vous recevrez un lien pour choisir l'horaire qui vous convient.",
      spec: "SN/06 · SÉCURISÉ · RÉSERVATION"
    }, {
      id: "07",
      category: "immigration",
      question: "Pouvez-vous intervenir pour des dossiers d'immigration urgents ?",
      answer: "Pour les dossiers sensibles aux délais, j'assure un service d'examen prioritaire afin de garantir la conformité et le dépôt de vos dossiers dans les délais impartis, évitant ainsi les retards de procédure.",
      spec: "SN/07 · PRIORITÉ · EXAMEN"
    }, {
      id: "08",
      category: "haitian-law",
      question: "Fournissez-vous des services pour les entreprises ?",
      answer: "J'accompagne les entreprises dans leur conformité au droit étranger, garantissant que les opérations commerciales internationales en Haïti respectent les réglementations locales et les traités internationaux.",
      spec: "SN/08 · ENTREPRISE · CONFORMITÉ"
    }]
  },
  ht: {
    sectionLabel: "§ FAQ / ENFÒMASYON JIRIDIK",
    heading: "Analiz jiridik pa ekspè.",
    categoryIndex: "※ LIS KATEGORI",
    categories: [{
      id: "all",
      label: "TOUT"
    }, {
      id: "compliance",
      label: "KONFÒMITE"
    }, {
      id: "immigration",
      label: "IMIGRASYON"
    }, {
      id: "haitian-law",
      label: "LWA AYISYEN"
    }, {
      id: "consultation",
      label: "KONSILTASYON"
    }],
    entries: "KANTITE",
    status: "ESTATI",
    verified: "VERIFYE",
    directChannel: "※ KANAL DIRÈK",
    directChannelHeading: "Ou bezwen konsèy pwofesyonèl?",
    bookConsultation: "PRAN YON KONSILTASYON",
    faqs: [{
      id: "01",
      category: "compliance",
      question: "Kisa sètifikasyon FCFLC a vle di?",
      answer: "Kòm Konsiltan Jiridik Etranje ki sètifye pa Baro Florid la, mwen otorize pou bay konsèy ekspè sou lwa jiridiksyon etranje yo, patikilyèman sou lwa ayisyen ak nòm konfòmite entènasyonal yo.",
      spec: "SN/01 · BARO FLORID · SÈTIFYE"
    }, {
      id: "02",
      category: "immigration",
      question: "Kouman ou ka ede m ak dokiman imigrasyon yo?",
      answer: "Mwen bay sipò konplè nan demach imigrasyon ki konplèks, pou asire tout dokiman byen prepare, konfòm ak nòm entènasyonal yo, epi adapte ak objektif jiridik ou.",
      spec: "SN/02 · DOKIMANTASYON · GIDANS"
    }, {
      id: "03",
      category: "haitian-law",
      question: "Èske ou espesyalize nan zafè jiridik ayisyen?",
      answer: "Wi, pratik mwen chita dirèkteman nan lwa ayisyen. Mwen ede kliyan yo nan konfli tè ak pwopriyete, konfòmite antrepriz, ak zafè sivil, pou fè pon ant lwa ayisyen ak egzijans entènasyonal yo.",
      spec: "SN/03 · LWA AYISYEN · ESPESYALIS"
    }, {
      id: "04",
      category: "consultation",
      question: "Kisa fòmasyon akademik ou pote kòm valè?",
      answer: "Metriz mwen nan Jistis Kriminèl ak Etid Imigrasyon nan University of North Florida bay yon baz akademik solid ki gide apwòch mwen nan rezoud defi jiridik entènasyonal ki konplèks.",
      spec: "SN/04 · UNF · METRIZ"
    }, {
      id: "05",
      category: "compliance",
      question: "Èske konsiltasyon yo rete konfidansyèl?",
      answer: "Sekrè pwofesyonèl ak konfidansyalite total se poto mitan travay mwen. Tout konsiltasyon fèt ak pi gwo diskresyon pou pwoteje enterè jiridik ou yo.",
      spec: "SN/05 · KONFIDANSYÈL · SEKIRIZE"
    }, {
      id: "06",
      category: "consultation",
      question: "Kouman pou mwen pran yon randevou konsiltasyon?",
      answer: "Ou ka rezève yon sesyon konsiltasyon dirèkteman sou pòtay nou an. Kou peman an konfime, w ap resevwa yon lyen pou w chwazi yon lè ki mache ak orè w.",
      spec: "SN/06 · SEKIRIZE · REZÈVASYON"
    }, {
      id: "07",
      category: "immigration",
      question: "Èske ou ka ede nan dosye imigrasyon ki ijan?",
      answer: "Pou zafè imigrasyon ki mande vitès, mwen ofri sèvis revizyon priyoritè pou asire dosye w yo kòrèk epi depoze nan delè yo mande a, sa ki redui risk reta nan pwosedi a.",
      spec: "SN/07 · PRIYORITE · REVIZYON"
    }, {
      id: "08",
      category: "haitian-law",
      question: "Èske ou ofri sèvis pou biznis ak konpayi?",
      answer: "Mwen sipòte konpayi yo nan konfòmite ak lwa etranje yo, pou asire aktivite biznis entènasyonal an Ayiti respekte lwa lokal yo ak trete entènasyonal yo.",
      spec: "SN/08 · KÒPORATIF · KONFÒMITE"
    }]
  }
};
export default function ConsultationFaq() {
  const {
    lang
  } = useLanguage();
  const currentLang = lang as keyof typeof translations || "en";
  const t = translations[currentLang] || translations.en;
  const [activeCategory, setActiveCategory] = useState("all");
  const filteredFaqs = activeCategory === "all" ? t.faqs : t.faqs.filter(f => f.category === activeCategory);
  return <section data-section-id="4978" id="consultation-faq" className="bg-background text-foreground py-20 md:py-[140px] px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="mb-16 md:mb-24">
          <div className="font-mono text-xs uppercase tracking-widest text-primary mb-6">
            {t.sectionLabel}
          </div>
          <h2 className="font-default text-4xl md:text-5xl lg:text-[56px] font-semibold leading-[1.05] tracking-tight text-foreground">
            {t.heading}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-6 pb-4 border-b border-border">
                {t.categoryIndex}
              </div>
              <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
                {t.categories.map((cat, i) => {
                const isActive = activeCategory === cat.id;
                return <Button data-index={i} key={cat.id} onClick={() => setActiveCategory(cat.id)} variant="ghost" className={`font-mono text-xs uppercase tracking-widest text-left h-auto py-3 px-4 border-l-2 rounded-md justify-start transition-all whitespace-nowrap ${isActive ? "border-l-primary text-foreground bg-card" : "border-l-transparent text-muted-foreground hover:text-foreground hover:border-l-border hover:bg-transparent"}`}>
                      <span className="opacity-50 mr-3">
                        {String(i).padStart(2, "0")}
                      </span>
                      {cat.label}
                    </Button>;
              })}
              </nav>

              <div className="mt-10 pt-6 border-t border-border hidden lg:block">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground space-y-2">
                  <div className="flex justify-between">
                    <span>{t.entries}</span>
                    <span className="text-foreground">
                      {String(filteredFaqs.length).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t.status}</span>
                    <span className="text-primary">{t.verified}</span>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {filteredFaqs.map((faq, i) => <AccordionItem data-index={i} key={faq.id} value={faq.id} className="border-b-0 border-t border-border first:border-t group/item">
                  <AccordionTrigger className="hover:no-underline py-6 [&>svg]:hidden group">
                    <div className="flex items-start gap-6 md:gap-10 w-full pr-4">
                      <span className="font-mono text-xs text-muted-foreground mt-1 shrink-0">
                        {faq.id}
                      </span>
                      <span className="font-default text-base md:text-lg text-foreground text-left flex-1 transition-transform duration-200 group-hover:translate-x-1 group-data-[state=open]:translate-x-1">
                        {faq.question}
                      </span>
                      <span className="shrink-0 mt-1">
                        <Plus className="size-4 text-primary transition-transform duration-300 group-data-[state=open]:rotate-45" strokeWidth={1.5} />
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <div className="ml-0 md:ml-[3.25rem] pl-6 md:pl-10 border-l border-border space-y-4">
                      <p className="font-default text-[15px] leading-relaxed text-muted-foreground">
                        {faq.answer}
                      </p>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-primary/80">
                        {faq.spec}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>

            <div className="mt-12 border border-primary bg-card p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6 rounded-md">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-primary mb-2">
                  {t.directChannel}
                </div>
                <p className="font-mono text-sm uppercase tracking-wider text-foreground">
                  {t.directChannelHeading}
                </p>
              </div>
              <Button asChild variant="ghost" className="font-mono text-xs uppercase tracking-widest text-primary hover:text-primary hover:bg-primary/10 self-start md:self-auto px-4 h-auto py-2">
                <Link to="#">
                  {t.bookConsultation}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}